import React, { useState, useContext, useEffect, useCallback } from 'react';
import ModalContext from '../../../../contexts/ModalContext';
import { useOutletContext } from 'react-router-dom';
import { ProductionFashionOutletType } from '..';
import { GlobalTable, HeaderLine, Td, Th, Tr } from '../../../../components/styles/table-style';
import { Actions, Select, SelectArrow, SelectContainer } from './style';
import { Undo2 } from 'lucide-react';
import arrowDown from '../../../../assets/addStockModal/excel/arrow-down.svg';
import search from '../../../../assets/productions/search-in.svg';
import play from '../../../../assets/production-flow/play-circle.svg';
import FashionApiService from '../../../../services/FashionApiService';
import eventBus from '../../../../utils/eventBus';
import LoadMoreButton from '../../../../components/LoadMoreButton';
import { LoadMoreProvider, useLoadMore } from '../../../../contexts/LoadMoreContext';
import { TableContainer } from '../style';
import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import ConfirmationModal from '../../../../components/ModalConfirmation';


export interface CompanyData {
  id: number;
  companyName: string;
}

interface FashionProductionItemsData {
  id: number;
  serviceId: number;
  fashionOrderId: number;
  quantityForThisProduction: number;
  fashionOrder?: {
    id: number;
    Company_Order_clientIdToCompany?: { companyName: string };
  };
}

interface UserProfileData {
  id: number;
  name: string;
}

interface FashionProductionOrderData {
  id: number;
  set: string;
  status: number;
  dressmakerId: number | null;
  cutterId: number | null;
  productId?: number | null;
  collectionColorId?: number | null;
  codeOfOP?: string;

  Product?: { id: number; name: string } | null;
  CollectionColor?: { id: number; name: string } | null;
  User_Services_cutterIdToUser?: UserData | null;
  User_Services_dressmakerIdToUser?: UserData | null;
  FashionProductionItems?: FashionProductionItemsData[];

  totalItems?: number;
  materialsAvailable?: boolean | null;
}

interface UserData {
  id: number;
  name: string; 
  email?: string;
  profile?: UserProfileData;
  profileId?: number | null;
}


const ProductionFashionTableContent: React.FC = () => {
  const { setTabIndex } = useOutletContext<ProductionFashionOutletType>();
  const { setModalActive, setModalSection, setSelectedProductionOrder } = useContext(ModalContext);
  const { hasNextPage, isFetchingNextPage, setIsFetchingNextPage, setFetchNextPageFunction, fetchNextPage, setHasNextPage } = useLoadMore();
  const { setFeedback } = useContext(FeedbackContext);

  const columns = ["OP", "Pedido", "Produto", "Cor", "Qtd. de itens", "Status", "Cortador", "Costureira", "", ""];

  const [productionOrders, setProductionOrders] = useState<FashionProductionOrderData[]>([]);
  const [cuttersList, setCuttersList] = useState<UserData[]>([]);
  const [dressmakersList, setDressmakersList] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const fetchData = useCallback(async (currentPage: number = 0, append: boolean = false) => {
    try {
      setIsLoading(currentPage === 0);
      if (currentPage > 0) setIsFetchingNextPage(true);
      setError(null);

      console.log('🔍 [FETCH DATA] currentPage:', currentPage, 'append:', append);

      const totalResponse = await FashionApiService.getProductionOrdersCount();
      const total = totalResponse.data?.total || 0;
      setTotalCount(total);

      const productionOrdersResponse = await FashionApiService.getProductionOrders(currentPage, pageSize);

      const [cuttersResp, dressmakersResp] = await Promise.all([
        FashionApiService.getUsers('Cortador'),
        FashionApiService.getUsers('Costureira')
      ]);

      const cutters: UserData[] = cuttersResp.data || [];
      const dressmakers: UserData[] = dressmakersResp.data || [];

      if (cutters.length === 0) console.warn("Nenhum cortador retornado pelo backend para este tenant.");
      if (dressmakers.length === 0) console.warn("Nenhuma costureira retornada pelo backend para este tenant.");

      if (currentPage === 0) {
        setCuttersList([{ id: 0, name: "Selecione..." }, ...cutters]);
        setDressmakersList([{ id: 0, name: "Selecione..." }, ...dressmakers]);
      }

      const mappedOrders: FashionProductionOrderData[] = productionOrdersResponse.data.map((order: FashionProductionOrderData) => {
        const totalItemsInProduction = order.FashionProductionItems?.reduce((sum, item) => sum + item.quantityForThisProduction, 0) || 0;

        return {
          ...order,
          totalItems: totalItemsInProduction,
          materialsAvailable: null,
        };
      });

      const returnedItemsCount = mappedOrders.length;
      const hasMoreData = returnedItemsCount === pageSize;
      
      if (append) {
        setProductionOrders(prev => {
          const existingIds = new Set(prev.map(o => o.id));
          const newOrders = mappedOrders.filter(o => !existingIds.has(o.id));
          const updatedOrders = [...prev, ...newOrders];
          const hasMore = updatedOrders.length < total && hasMoreData;
          setHasNextPage(hasMore);
          console.log('➕ [PRODUCTION] Append - hasMore:', hasMore, 'prev.length:', prev.length, 'newOrders.length:', newOrders.length, 'updatedOrders.length:', updatedOrders.length, 'total:', total, 'hasMoreData:', hasMoreData);
          return updatedOrders;
        });
      } else {
        setProductionOrders(mappedOrders);
        const hasMore = mappedOrders.length < total && hasMoreData;
        setHasNextPage(hasMore);
        console.log('🆕 [PRODUCTION] First load - hasMore:', hasMore, 'mappedOrders.length:', mappedOrders.length, 'total:', total, 'hasMoreData:', hasMoreData);
      }

      const availabilityResults = await Promise.allSettled(
        mappedOrders.map(order =>
          FashionApiService.getMaterialAvailability(order.id)
            .then(res => ({ id: order.id, available: res.data.available as boolean }))
        )
      );

      setProductionOrders(prev => {
        const availabilityMap = new Map<number, boolean>();
        availabilityResults.forEach((result, i) => {
          if (result.status === 'fulfilled') {
            availabilityMap.set(result.value.id, result.value.available);
          } else {
            availabilityMap.set(mappedOrders[i].id, false);
          }
        });

        if (append) {
          return prev.map(order =>
            availabilityMap.has(order.id)
              ? { ...order, materialsAvailable: availabilityMap.get(order.id) ?? null }
              : order
          );
        } else {
          return prev.map(order => ({
            ...order,
            materialsAvailable: availabilityMap.get(order.id) ?? null,
          }));
        }
      });

    } catch (err: any) {
      console.error('Erro ao buscar dados:', err);
      setError('Falha ao carregar ordens de produção ou usuários.');
    } finally {
      setIsLoading(false);
      setIsFetchingNextPage(false);
    }
  }, [pageSize, setIsFetchingNextPage, setHasNextPage]);

  useEffect(() => {
    const handleRefreshTable = () => {
      setPage(0);
      fetchData(0, false);
    };

    eventBus.on('refreshProductionTable', handleRefreshTable);

    return () => {
      eventBus.off('refreshProductionTable', handleRefreshTable);
    };
  }, [fetchData]);

  useEffect(() => {
    fetchData(0, false);
  }, []);

  useEffect(() => {
    if (page > 0) {
      console.log('📄 [PAGE CHANGED] Fetching page:', page);
      fetchData(page, true);
    }
  }, [page, fetchData]);

  useEffect(() => {
    const loadMoreFunction = () => {
      if (!isFetchingNextPage) {
        console.log('🔼 [LOAD MORE] Current page:', page, 'Setting to:', page + 1);
        setPage((prevPage) => prevPage + 1);
      }
    };
    setFetchNextPageFunction(loadMoreFunction);
  }, [setFetchNextPageFunction, page, isFetchingNextPage]);


  useEffect(() => {
    if (productionOrders.length > 0 && cuttersList.length > 1 && dressmakersList.length > 1) { // >1 para ignorar "Selecione..."
      productionOrders.forEach(orderItem => {
        const assignedCutter = cuttersList.find(c => c.id === orderItem.cutterId);
        const assignedDressmaker = dressmakersList.find(d => d.id === orderItem.dressmakerId);
      });
    }
  }, [productionOrders, cuttersList, dressmakersList]);

  const handleCortadorChange = async (e: React.ChangeEvent<HTMLSelectElement>, orderId: number) => {
    const selectedUserId = Number(e.target.value);
    const selectedUserObject = cuttersList.find(c => c.id === selectedUserId);

    setProductionOrders(prevOrders =>
      prevOrders.map(order =>
        order.id === orderId ? {
          ...order,
          cutterId: selectedUserId === 0 ? null : selectedUserId,
          User_Services_cutterIdToUser: selectedUserId === 0 ? null : selectedUserObject
        } : order
      )
    );

    try {
      await FashionApiService.updateProductionOrder(orderId, {
        cutterId: selectedUserId === 0 ? null : selectedUserId,
      });
      const newCutterName = selectedUserObject?.name || 'Nenhum';
      setFeedback({ isOpen: true, message: `Cortador da OP-${orderId} atualizado para ${newCutterName}!`, statusOk: true });
    } catch (err) {
      console.error(`Erro ao atualizar cortador da OP ${orderId}:`, err);
      setFeedback({ isOpen: true, message: `Falha ao atualizar cortador da OP-${orderId}.`, statusOk: false });
      fetchData();
    }
  };

  const handleCostureiraChange = async (e: React.ChangeEvent<HTMLSelectElement>, orderId: number) => {
    const selectedUserId = Number(e.target.value);
    const selectedUserObject = dressmakersList.find(d => d.id === selectedUserId);

    setProductionOrders(prevOrders =>
      prevOrders.map(order =>
        order.id === orderId ? {
          ...order,
          dressmakerId: selectedUserId === 0 ? null : selectedUserId,
          User_Services_dressmakerIdToUser: selectedUserId === 0 ? null : selectedUserObject
        } : order
      )
    );

    try {
      await FashionApiService.updateProductionOrder(orderId, {
        dressmakerId: selectedUserId === 0 ? null : selectedUserId,
      });
      const newDressmakerName = selectedUserObject?.name || 'Nenhum';
      setFeedback({ isOpen: true, message: `Costureira da OP-${orderId} atualizada para ${newDressmakerName}!`, statusOk: true });
    } catch (err) {
      console.error(`Erro ao atualizar costureira da OP ${orderId}:`, err);
      setFeedback({ isOpen: true, message: `Falha ao atualizar costureira da OP-${orderId}.`, statusOk: false });
      fetchData();
    }
  };

  const handleView = (order: FashionProductionOrderData) => {
    setSelectedProductionOrder(order);
    setModalActive(true);
    setModalSection('viewProductionFashion');
  };

  const getStatusLabel = (status: number): string => {
    const statusMap: { [key: number]: string } = {
      0: 'Pendente',
      1: 'Iniciado',
      2: 'Concluído',
    };
    return statusMap[status] || String(status);
  };

  const [startingOrderId, setStartingOrderId] = useState<number | null>(null);

  const handleStartProduction = (orderId: number) => {
    setConfirmModal({
      isOpen: true,
      title: 'Iniciar Produção',
      message: `Iniciar a produção da OP-${orderId}? As ordens de compra serão geradas automaticamente a partir da composição.`,
      onConfirm: async () => {
        setStartingOrderId(orderId);
        try {
          await FashionApiService.startFashionProduction(orderId);
          eventBus.emit('refreshProductionTable');
          setFeedback({ isOpen: true, message: `OP-${orderId} iniciada. Ordens de compra provisionadas.`, statusOk: true });
        } catch (err: any) {
          console.error(`Erro ao iniciar OP ${orderId}:`, err);
          setFeedback({ isOpen: true, message: err?.response?.data?.message || `Falha ao iniciar OP-${orderId}.`, statusOk: false });
        } finally {
          setStartingOrderId(null);
        }
      }
    });
  };

  const handleRevertStartProduction = (orderId: number) => {
    setConfirmModal({
      isOpen: true,
      title: 'Reverter Início da Produção',
      message: `Reverter o início da OP-${orderId}? As ordens de compra ainda pendentes serão removidas.`,
      onConfirm: async () => {
        setStartingOrderId(orderId);
        try {
          await FashionApiService.revertFashionProductionStart(orderId);
          eventBus.emit('refreshProductionTable');
          setFeedback({ isOpen: true, message: `OP-${orderId} revertida com sucesso.`, statusOk: true });
        } catch (err: any) {
          console.error(`Erro ao reverter OP ${orderId}:`, err);
          setFeedback({ isOpen: true, message: err?.response?.data?.message || `Falha ao reverter OP-${orderId}.`, statusOk: false });
        } finally {
          setStartingOrderId(null);
        }
      }
    });
  };

  if (isLoading) {
    return <div>Carregando ordens de produção...</div>;
  }

  if (error) {
    return <div>Erro: {error}</div>;
  }

  return (
    <TableContainer>
      <GlobalTable>
        <thead>
          <HeaderLine>
            {columns.map((item, index) => (
              <Th key={index}>{item}</Th>
            ))}
          </HeaderLine>
        </thead>
        <tbody>
          {productionOrders.map((orderItem) => (
            <Tr key={orderItem.id}>
              <Td>{`OP-${orderItem.id}`}</Td>
              <Td>
                {orderItem.FashionProductionItems && orderItem.FashionProductionItems.length > 0
                  ? orderItem.FashionProductionItems.map(item => `PED-${item.fashionOrderId}`).join(', ')
                  : 'N/A'}
              </Td>
              <Td>{orderItem.Product?.name || 'N/A'}</Td>
              <Td>{orderItem.CollectionColor?.name || 'N/A'}</Td>
              <Td>{orderItem.totalItems}</Td>
              <Td>{getStatusLabel(orderItem.status)}</Td>
              <Td>
                <SelectContainer>
                  <Select
                    value={orderItem.cutterId || 0}
                    onChange={(e) => handleCortadorChange(e, orderItem.id)}
                  >
                    {cuttersList.map((cutter) => (
                      <option key={cutter.id} value={cutter.id}>
                        {cutter.name}
                      </option>
                    ))}
                  </Select>
                  <SelectArrow src={arrowDown} alt="Seta para baixo" />
                </SelectContainer>
              </Td>
              <Td>
                <SelectContainer>
                  <Select
                    value={orderItem.dressmakerId || 0}
                    onChange={(e) => handleCostureiraChange(e, orderItem.id)}
                  >
                    {dressmakersList.map((dressmaker) => (
                      <option key={dressmaker.id} value={dressmaker.id}>
                        {dressmaker.name}
                      </option>
                    ))}
                  </Select>
                  <SelectArrow src={arrowDown} alt="Seta para baixo" />
                </SelectContainer>
              </Td>
              <Td style={{ textAlign: 'center' }}>
                <span
                  title={
                    orderItem.materialsAvailable === null || orderItem.materialsAvailable === undefined
                      ? 'Verificando...'
                      : orderItem.materialsAvailable
                      ? 'Materiais suficientes'
                      : 'Materiais insuficientes'
                  }
                  style={{
                    color: orderItem.materialsAvailable === null || orderItem.materialsAvailable === undefined
                      ? '#9ca3af'
                      : orderItem.materialsAvailable
                      ? '#adf0c7'
                      : '#ffc6c6',
                    fontSize: '1.6rem',
                    lineHeight: 1,
                    cursor: 'default',
                  }}
                >
                  ⬤
                </span>
              </Td>
              <Td>
                <Actions>
                  <img onClick={() => handleView(orderItem)} src={search} alt="Visualizar ordem" style={{ cursor: 'pointer' }} />
                  {orderItem.status === 1 ? (
                    <Undo2
                      size={22}
                      strokeWidth={2}
                      color={startingOrderId === orderItem.id ? '#9ca3af' : '#0090d3'}
                      onClick={() => {
                        if (startingOrderId !== null) return;
                        handleRevertStartProduction(orderItem.id);
                      }}
                      style={{
                        cursor: startingOrderId !== null ? 'wait' : 'pointer',
                        opacity: startingOrderId !== null && startingOrderId !== orderItem.id ? 0.5 : 1,
                      }}
                      aria-label="Reverter início da produção"
                    />
                  ) : (
                    <img
                      onClick={() => {
                        if (startingOrderId !== null) return;
                        handleStartProduction(orderItem.id);
                      }}
                      src={play}
                      alt="Iniciar ordem"
                      style={{
                        cursor: startingOrderId !== null ? 'wait' : 'pointer',
                        opacity: startingOrderId !== null && startingOrderId !== orderItem.id ? 0.5 : 1,
                      }}
                    />
                  )}
                </Actions>
              </Td>
            </Tr>
          ))}
        </tbody>
      </GlobalTable>
      <LoadMoreButton
        currentCount={productionOrders.length}
        totalCount={totalCount}
        onLoadMore={fetchNextPage}
        isLoading={isFetchingNextPage}
        hasMore={hasNextPage}
      />
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        actions={[
          {
            label: 'Cancelar',
            onClick: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
            variant: 'outline',
          },
          {
            label: 'Confirmar',
            onClick: () => {
              confirmModal.onConfirm();
              setConfirmModal(prev => ({ ...prev, isOpen: false }));
            },
            variant: 'primary',
          },
        ]}
      />
    </TableContainer>
  );
};

const ProductionFashionTable: React.FC = () => (
  <LoadMoreProvider>
    <ProductionFashionTableContent />
  </LoadMoreProvider>
);

export default ProductionFashionTable;